import React, { useState, useEffect } from "react";
import { withTranslation } from 'react-i18next';
import {
  Row, Col, Card, CardBody, FormGroup, Button, CardTitle,
  CardSubtitle, Label, Input, Container, Form, FormFeedback,
  UncontrolledAlert, Spinner
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { get, post } from "../../Services/RestAPIService";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

const CreateSubCategories = ({ t }) => {
  const [categoryImage, setCategoryImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataOriginal, setDataOriginal] = useState([]);
  const [selectedData, setSelectedData] = useState(null); // State for selected data

  const navigate = useNavigate();
  const { id } = useParams();
  const [alert, setAlert] = useState({ color: "", message: "", visible: false, strongM: "" });

  const getUsersData = async () => {
    try {
      const usersData = await get(`category/categories/?parent_id_isnull=true`);
      if (usersData) {
        let formattedData = usersData.results.map((category) => ({
          label: category.name,
          value: category.id.toString(),
        }));
        setDataOriginal(formattedData);
      } else {
        console.error('No data found in the response.');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    getUsersData();
  }, []);

  const validation = useFormik({
    initialValues: {
      name: "",
      description: "",
      categorySelected: null, // Add the categorySelected field for validation
    },

    validationSchema: Yup.object({
      name: Yup.string().required(t('Please Enter Category Name')),
      description: Yup.string().required(t('Please Enter Description')),
   categorySelected: (id === "id" ? Yup.object().required(t('Please select a parent category') ):null) , // Ensuring categorySelected is required
    }),

    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      const data = {
        name: values.name,
        description: values.description,
        image: categoryImage,
        parent_id: Number(selectedData?.value) || id,
      };

      try {
        const response = await post("category/categories/", data, {
          headers: { "Content-Type": "application/json" },
          validateStatus: false,
        });
        if (response.status !== 400) {
          setAlert({
            color: "success",
            visible: true,
            message: t('New category created successfully! Check All Sub-category'),
            strongM: t('Success!')
          });
          setTimeout(() => setAlert({ visible: false }), 3000);
          setTimeout(() => navigate(`/category/sub-category/${selectedData?.value || id}`), 2000);
          resetForm();
          setCategoryImage(null);
        } else {
          let errorMessage = t('An error occurred while updating category data.');
          for (const key in response.data) {
            if (Array.isArray(response.data[key]) && response.data[key].length > 0) {
              errorMessage = `${key}: ${response.data[key][0]}`;
              break;
            }
          }
          setAlert({ color: "danger", visible: true, message: errorMessage, strongM: t('Error!') });
          setTimeout(() => setAlert({ visible: false }), 3000);
        }
      } catch (error) {
        let errorMessage = t('An error occurred while updating category data.');
        setAlert({ color: "danger", visible: true, message: errorMessage, strongM: t('Error!') });
        setTimeout(() => setAlert({ visible: false }), 3000);
      } finally {
        setIsLoading(false);
      }
    }
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCategoryImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <React.Fragment>
      {alert.visible && <UncontrolledAlert color={alert.color}>{alert.message}</UncontrolledAlert>}
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs maintitle="Home" title="Pain Area Body Parts" titleLink={`/category/sub-category/${id}`} breadcrumbItem={t('Add Body Part')} />
          <Row>
            <Col lg={6}>
              <Card>
                <CardBody>
                  <CardTitle>{t('Add New Body Part')}</CardTitle>
                  <CardSubtitle className="mb-3">{t('You can Add New Body Part from here.')}</CardSubtitle>
                  <Form onSubmit={validation.handleSubmit}>
                    {id === "id" && (
                      <FormGroup className="mb-3">
                        <Label className="form-label">{t('Select Parent Category')}</Label>
                        <Select
                          value={selectedData}
                          name="categorySelected"
                          onChange={(selectedOption) => {
                            setSelectedData(selectedOption);
                            validation.setFieldValue('categorySelected', selectedOption); // Set formik value
                          }}
                          onBlur={validation.handleBlur}
                          options={dataOriginal}
                          classNamePrefix="select2-selection"
                        />
                        {validation.touched.categorySelected && validation.errors.categorySelected && (
                          <FormFeedback type="invalid">{validation.errors.categorySelected}</FormFeedback>
                        )}
                      </FormGroup>
                    )}

                    <FormGroup className="mb-3">
                      <Label>{t('Body Part Name')}</Label>
                      <Input
                        type="text"
                        name="name"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.name}
                        invalid={validation.touched.name && validation.errors.name}
                      />
                      {validation.touched.name && validation.errors.name && (
                        <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label>{t('Description')}</Label>
                      <Input
                        type="textarea"
                        name="description"
                        rows="5"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.description}
                        invalid={validation.touched.description && validation.errors.description}
                      />
                      {validation.touched.description && validation.errors.description && (
                        <FormFeedback type="invalid">{validation.errors.description}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label>{t('Image')}</Label>
                      <Input type="file" name="image" onChange={handleImageChange} />
                    </FormGroup>

                    <Button type="submit" color="primary" disabled={isLoading}>
                      {isLoading ? <Spinner size="sm" color="light" /> : t('Create')}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(CreateSubCategories);
